import { useQuery } from '@tanstack/react-query';

import { canAddArtifact } from '@/features/artifacts/artifact-display.js';
import { knowledgeSourcesQueryOptions } from '@/lib/api/workspace-knowledge.js';

import {
  findSharePointKnowledgeSource,
  type SharePointAddress,
} from './knowledge-address.js';

/**
 * "This one is in the workspace's knowledge base already" — the one place that answers it, for every
 * SharePoint surface that shows rows: the Artifacts section's tab, the composer's Add-artifacts dialog and
 * the knowledge wizard.
 *
 * It is a fact about the ITEM, not about the surface, which is why it is computed here and not inside each
 * picker. Three copies of this question would drift apart the moment one of them matched on the item id
 * alone — and that is the mistake this rule exists to prevent, since a Graph `item_id` is unique only
 * inside its own drive.
 */

/** The words the mark is shown and announced in. One string: the badge and the accessible name agree. */
export const KNOWLEDGE_MARK_LABEL = 'In the knowledge base';

/** Answers the mark for one item, at the address the pair `drive_id` + `item_id` makes. */
export type SharePointKnowledgeMark = (address: SharePointAddress) => boolean;

/**
 * The mark for this workspace, ready to hand to a list.
 *
 * The shelf is read only for a caller who could add to it — {@link canAddArtifact}, the same predicate the
 * Artifacts section's Files tab gates its own shelf read with. Keeping the two identical is deliberate: a
 * viewer is offered nothing to do with the answer, and letting one surface read what the other refuses to
 * would be a worse inconsistency than the restriction itself. With the read off, every row is unmarked and
 * nothing else about the surface changes.
 *
 * `enabled` is the caller's own gate on top of that — a dialog keeps it off until it opens.
 */
export function useSharePointKnowledgeMark({
  workspaceId,
  role,
  enabled = true,
}: {
  workspaceId: string;
  /** The caller's role in this workspace, resolved without platform-admin elevation. */
  role: string | null;
  enabled?: boolean;
}): SharePointKnowledgeMark {
  const query = useQuery({
    ...knowledgeSourcesQueryOptions(workspaceId),
    enabled: enabled && canAddArtifact(role),
  });
  const sources = query.data ?? [];
  return (address) =>
    findSharePointKnowledgeSource(sources, address) !== undefined;
}
